<template>
  <transition name="fullscreen-search-fade">
    <div v-if="visible" class="fullscreen-search" @keydown.esc="handleClose">
      <button type="button" class="fullscreen-search__close" aria-label="关闭搜索" @click="handleClose">
        <i class="el-icon-close" />
      </button>

      <div class="fullscreen-search__content">
        <div class="fullscreen-search__bar">
          <el-select
            v-model="searchScope"
            class="fullscreen-search__select"
            popper-class="fullscreen-search-popper"
          >
            <el-option
              v-for="item in scopeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <input
            ref="keywordInput"
            v-model="keyword"
            type="text"
            class="fullscreen-search__input"
            placeholder="请输入关键词"
            @keyup.enter="handleSubmit"
          />
          <button type="button" class="fullscreen-search__submit" @click="handleSubmit">
            <i class="el-icon-search" />
            <span>搜索</span>
          </button>
        </div>

        <div class="fullscreen-search__categories">
          <button
            v-for="item in categoryLinks"
            :key="item.value"
            type="button"
            class="fullscreen-search__category"
            @click="handleCategoryClick(item.value)"
          >
            <span class="fullscreen-search__category-label">{{ item.label }}</span>
            <span
              class="fullscreen-search__category-count"
              :class="{ 'fullscreen-search__category-count--loading': isCountLoading(item.value) }"
            >
              <span v-if="isCountLoading(item.value)" class="fullscreen-search__count-loader">
                <i /><i /><i />
              </span>
              <span v-else class="fullscreen-search__count-value">
                {{ formatCount(getDisplayCount(item.value)) }}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  SEARCH_SCOPE_OPTIONS,
  SEARCH_CATEGORY_LINKS,
  navigateBySearchScope
} from '@/utils/searchNavigation'
import { searchKnowledgeEntities } from '@/api/knowledge'
import { searchResources } from '@/api/resources'
import { fetchExhibitionList } from '@/api/exhibition'

const MODULE_KEYS = ['knowledge', 'resources', 'exhibition']

function createCountState() {
  return MODULE_KEYS.reduce((acc, key) => {
    acc[key] = { loading: false, total: 0, display: 0 }
    return acc
  }, {})
}

export default {
  name: 'FullscreenSearch',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchScope: 'all',
      keyword: '',
      scopeOptions: SEARCH_SCOPE_OPTIONS,
      categoryLinks: SEARCH_CATEGORY_LINKS,
      moduleCounts: createCountState(),
      countAnimIds: {},
      fetchTimer: null,
      fetchToken: 0
    }
  },
  computed: {
    visible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    value(open) {
      document.body.style.overflow = open ? 'hidden' : ''
      if (open) {
        this.$nextTick(() => {
          if (this.$refs.keywordInput) {
            this.$refs.keywordInput.focus()
          }
        })
        this.scheduleFetchCounts()
      } else {
        this.clearFetchTimer()
        this.cancelCountAnimations()
      }
    },
    keyword() {
      if (this.visible) {
        // this.scheduleFetchCounts()
      }
    }
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    this.clearFetchTimer()
    this.cancelCountAnimations()
  },
  methods: {
    isCountLoading(moduleKey) {
      return !!(this.moduleCounts[moduleKey] && this.moduleCounts[moduleKey].loading)
    },
    getDisplayCount(moduleKey) {
      return (this.moduleCounts[moduleKey] && this.moduleCounts[moduleKey].display) || 0
    },
    formatCount(value) {
      return Number(value || 0).toLocaleString('zh-CN')
    },
    scheduleFetchCounts() {
      this.clearFetchTimer()
      this.fetchTimer = setTimeout(() => {
        this.fetchModuleCounts()
      }, 280)
    },
    clearFetchTimer() {
      if (this.fetchTimer) {
        clearTimeout(this.fetchTimer)
        this.fetchTimer = null
      }
    },
    setModuleLoading(moduleKey, loading) {
      if (!this.moduleCounts[moduleKey]) return
      this.$set(this.moduleCounts[moduleKey], 'loading', loading)
      if (loading) {
        this.$set(this.moduleCounts[moduleKey], 'display', 0)
      }
    },
    async fetchModuleCounts() {
      const token = ++this.fetchToken
      const keyword = this.keyword.trim()

      MODULE_KEYS.forEach((key) => this.setModuleLoading(key, true))
      this.cancelCountAnimations()

      const requests = {
        knowledge: searchKnowledgeEntities({ page: 1, limit: 1, keyword: keyword || undefined }),
        resources: searchResources({
          page: 1,
          limit: 1,
          keyword: keyword || undefined,
          matchMode: 'fuzzy'
        }),
        exhibition: fetchExhibitionList({
          page: 1,
          limit: 1,
          title: keyword || undefined,
          status: '1,2'
        })
      }

      const results = await Promise.allSettled(
        MODULE_KEYS.map(async (key) => {
          const res = await requests[key]
          return res.total || 0
        })
      )

      if (token !== this.fetchToken) return

      MODULE_KEYS.forEach((key, index) => {
        const result = results[index]
        const total = result.status === 'fulfilled' ? result.value : 0
        this.$set(this.moduleCounts[key], 'loading', false)
        this.$set(this.moduleCounts[key], 'total', total)
        this.animateCount(key, total)
      })
    },
    animateCount(moduleKey, target) {
      this.cancelCountAnimation(moduleKey)
      const start = performance.now()
      const duration = Math.min(900, Math.max(420, Math.log10(target + 1) * 320))

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        const current = Math.round(target * eased)
        this.$set(this.moduleCounts[moduleKey], 'display', current)
        if (progress < 1) {
          this.countAnimIds[moduleKey] = requestAnimationFrame(step)
        } else {
          this.$set(this.moduleCounts[moduleKey], 'display', target)
          delete this.countAnimIds[moduleKey]
        }
      }

      this.countAnimIds[moduleKey] = requestAnimationFrame(step)
    },
    cancelCountAnimation(moduleKey) {
      if (this.countAnimIds[moduleKey]) {
        cancelAnimationFrame(this.countAnimIds[moduleKey])
        delete this.countAnimIds[moduleKey]
      }
    },
    cancelCountAnimations() {
      MODULE_KEYS.forEach((key) => this.cancelCountAnimation(key))
    },
    handleClose() {
      this.visible = false
    },
    handleSubmit() {
      const trimmed = (this.keyword || '').trim()
      if (!trimmed) {
        this.$message.warning('请输入搜索关键词')
        return
      }
      navigateBySearchScope(this.$router, this.searchScope, trimmed)
      this.handleClose()
    },
    handleCategoryClick(scope) {
      this.searchScope = scope
      navigateBySearchScope(this.$router, scope, this.keyword)
      this.handleClose()
    }
  }
}
</script>

<style lang="scss" scoped>
.fullscreen-search {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.72));

  &__close {
    position: absolute;
    top: 28px;
    right: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    color: $color-white;
    font-size: rem(28);
    transition: background 0.2s;

    &:hover {
      background: rgba($color-white, 0.12);
    }
  }

  &__content {
    width: min(920px, calc(100vw - 48px));
  }

  &__bar {
    display: flex;
    align-items: stretch;
    background: $color-white;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
  }

  &__select {
    width: 132px;
    flex-shrink: 0;

    ::v-deep .el-input__inner {
      height: 56px;
      border: none;
      border-radius: 0;
      box-shadow: none;
      font-size: rem(14);
    }
  }

  &__input {
    flex: 1;
    min-width: 0;
    padding: 0 18px;
    border: none;
    background: #f3f3f3;
    font-size: rem(15);
    color: $color-text;

    &::placeholder {
      color: #aaa;
    }
  }

  &__submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 48px;
    background: #4a6736;
    color: $color-white;
    font-size: rem(15);
    white-space: nowrap;
    transition: background 0.2s;

    &:hover {
      background: #3f5830;
    }
  }

  &__categories {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 36px;
    margin-top: 32px;
  }

  &__category {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-width: 88px;
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-2px);

      .fullscreen-search__category-label,
      .fullscreen-search__count-value {
        color: $color-white;
      }
    }
  }

  &__category-label {
    font-size: rem(15);
    color: rgba($color-white, 0.92);
    letter-spacing: 1px;
    transition: color 0.2s;
  }

  &__category-count {
    min-width: 48px;
    min-height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__count-value {
    font-family: $font-serif;
    font-size: rem(24);
    font-weight: 700;
    color: rgba($color-white, 0.95);
    letter-spacing: 1px;
    transition: color 0.2s;
  }

  &__count-loader {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;

    i {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: rgba($color-white, 0.85);
      animation: fullscreen-search-count-bounce 0.9s ease-in-out infinite;

      &:nth-child(2) {
        animation-delay: 0.15s;
      }

      &:nth-child(3) {
        animation-delay: 0.3s;
      }
    }
  }
}

@keyframes fullscreen-search-count-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.45;
  }

  40% {
    transform: translateY(-6px);
    opacity: 1;
  }
}

.fullscreen-search-fade-enter-active,
.fullscreen-search-fade-leave-active {
  transition: opacity 0.25s ease;
}

.fullscreen-search-fade-enter,
.fullscreen-search-fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
.fullscreen-search-popper {
  z-index: 2100 !important;
}
</style>
